import React, { useState } from 'react'

import { enrollmentHelperClient } from '@api/enrollmentClient'
import { getXCLEARCorrelationInfo } from '@api/utils'
import LookUpForm from '@components/pages/shared/LookUpForm'
import { Enrollment } from '@clear-denver/tpc-openapi-typescript'
import { useRouter } from 'next/router'
import { DiscreteEvent, dispatchCustomEvent, KPI } from '@utils/customEvents'
import PageContent from '@components/layout/PageContent'
import ApplicationFound from '@components/ApplicationFound'
import VerificationCodeForm from '@components/VerificationCodeForm'
import {
  ApplicantStatusDetails,
  OptAppstatusChallengeRequest,
} from '@clear-denver/tpc-openapi-typescript/build/v1/enrollment'
import { Modal } from '@components/shared/Modal'
import { PlanTypes, useEnrollmentContext } from '@contexts/EnrollmentContext'

interface FormData extends Enrollment.BiographicsLookup {
  otpSessionId: string
}

function StatusPage() {
  const [responseData, setResponseData] =
    useState<ApplicantStatusDetails | null>(null)
  const [showApplicationFound, setShowApplicationFound] =
    useState<boolean>(false)
  const [showVerificationForm, setShowVerificationForm] =
    useState<boolean>(false)
  const [verificationFormData, setVerificationFormData] =
    useState<FormData | null>(null)
  const [showModal, setShowErrorModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const { setSelectedPlan } = useEnrollmentContext()

  const router = useRouter()

  const onSubmitByBiographic = async (
    formData: Enrollment.BiographicsLookup
  ) => {
    setIsLoading(true)
    const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()
    const baseMetaData = {
      kpi: KPI.USER_INTERACTION,
      xCLEARCorrelationInfo,
      url: router.route,
    }
    const dob = new Date(formData.dob)
    dispatchCustomEvent(Enrollment.LogEntryLevelEnum.INFO, {
      payload: {
        message: 'Status lookup by biographics begin',
      },
      metadata: {
        ...baseMetaData,
        event: DiscreteEvent.STATUS_LOOKUP_BY_BIOGRAPHICS_BEGIN,
      },
    })

    try {
      const response =
        await enrollmentHelperClient.updatedAppStatusLookupBiographic({
          xCLEARCorrelationInfo,
          biographicsLookup: {
            ...formData,
            dob,
          },
        })

      if (response?.otpSessionId) {
        setVerificationFormData({
          ...formData,
          otpSessionId: response.otpSessionId,
        })
        setShowVerificationForm(true)
        setIsLoading(false)
      }
    } catch (error) {
      setIsLoading(false)
      setShowErrorModal(true)
    }
  }

  async function onVerificationSubmit(formData) {
    const xCLEARCorrelationInfo = getXCLEARCorrelationInfo()
    const requestBody: OptAppstatusChallengeRequest = {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      otpSessionId: verificationFormData!.otpSessionId,
      otpCode: formData.verificationCode,
      xCLEARCorrelationInfo,
    }

    enrollmentHelperClient
      .optAppstatusChallenge(requestBody)
      .then(async () => {
        const payload = {
          otpSessionId: requestBody.otpSessionId,
          xCLEARCorrelationInfo,
          biographicsLookup: {
            ...verificationFormData,
            ...formData,
          },
        }
        try {
          const response =
            await enrollmentHelperClient.updatedAppStatusLookupSession(payload)
          if (response) {
            setShowApplicationFound(true)
            setShowVerificationForm(false)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            setResponseData(response as any)
          }
        } catch (error) {
          setShowErrorModal(true)
        }
      })
      .catch((e) => {
        console.log(e)
        setShowErrorModal(true)
      })
  }

  const onClickEnroll = () => {
    setSelectedPlan(PlanTypes.ENROLL)
    router.push('/enroll/document-check')
  }

  const renderComponents = () => {
    if (showVerificationForm) {
      return (
        <PageContent>
          <VerificationCodeForm
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            formData={verificationFormData!}
            onSubmit={onVerificationSubmit}
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            onSubmitContact={() => onSubmitByBiographic(verificationFormData!)}
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            verificationFormData={verificationFormData!}
            isLoading={isLoading}
          />
        </PageContent>
      )
    }

    if (showApplicationFound && responseData != null) {
      return <ApplicationFound data={responseData} />
    }

    return (
      <LookUpForm
        headTitle="Check Status | TSA&nbsp;PreCheck® Enrollment Provided by CLEAR"
        headDescription="Check the status of your TSA&nbsp;PreCheck® membership application."
        title="Check Status"
        onSubmitByBiographic={onSubmitByBiographic}
      />
    )
  }

  return (
    <>
      {renderComponents()}
      <Modal
        showModal={showModal}
        setShowModal={setShowErrorModal}
        header="Information Not Found"
        body="We couldn’t find a record based on the information entered. If you have been issued a KTN and are attempting to renew, please confirm you’ve entered the correct information and try again. You can contact customer service at CLEARCustomerSupport@tsa.dhs.gov for additional assistance or begin a new enrollment."
        successButtonText="Enroll"
        canClose={true}
        successClick={onClickEnroll}
      />
    </>
  )
}

export default StatusPage
